import type { ConfigCtrlState } from '@debox-pro/chat-widget-core'
import type { DeBoxChat } from '@debox-pro/chat-widget-ui'

/**
 * Client
 */
export class Singleton {
  private static instance?: Singleton

  public tag: DeBoxChat | null = null

  public config: ConfigCtrlState = {
    zIndex: '999',
    projectId: '',
    conversationId: '',
    containerDomId: undefined,
    defaultOpen: false,
    destroyOnClose: false
  }

  public static getInstance(): Singleton {
    if (!Singleton.instance) {
      Singleton.instance = new Singleton()
    }

    return Singleton.instance
  }

  public initMain(config: ConfigCtrlState) {
    this.config = {
      ...this.config,
      ...config
    }

    this.tag = document.createElement('debox-chat-widget')
    this.tag.style.zIndex = this.config.zIndex as string
    this.tag.projectId = this.config.projectId
    this.tag.conversationId = this.config.conversationId
    this.tag.setAttribute('defaultOpen', `${this.config.defaultOpen}`)

    let containerDom = document.body
    if (this.config.containerDomId) {
      const container = document.getElementById(this.config.containerDomId)
      if (container) {
        containerDom = container
      }
    }
    containerDom.appendChild(this.tag)

    this.addCloseEvent()

    this.addToggleEvent()
  }

  public async init(config: ConfigCtrlState) {
    if (this.tag) {
      throw new Error('DeBoxChatWidget is already initialized')
    }

    this.initMain(config)

    if (typeof window !== 'undefined') {
      await import('@debox-pro/chat-widget-ui')
    }
  }

  public destroy() {
    if (this.tag) {
      this.removeToggleEvent()

      this.removeCloseEvent()

      if (this.callbackFn) {
        this.removeEventListener(this.callbackFn)
      }
      this.tag.remove()
      this.tag = null
    }

    this.config.onDestroy?.()

    this.config = {
      zIndex: '999',
      projectId: '',
      conversationId: '',
      containerDomId: undefined,
      defaultOpen: false,
      destroyOnClose: false
    }
  }

  public setConversation(id?: string, open?: boolean) {
    if (!this.tag) {
      throw new Error('DeBoxChatWidget is not initialized')
    }

    this.config.conversationId = id
    this.tag.conversationId = id

    if (typeof open === 'boolean') {
      this.tag.toggleChat(open)
    }
  }

  public toggleChat(open?: boolean) {
    if (!this.tag) {
      throw new Error('DeBoxChatWidget is not initialized')
    }

    this.tag.toggleChat(open)
  }

  private callbackFn?: EventListenerOrEventListenerObject
  public addEventListener(callback: EventListenerOrEventListenerObject) {
    if (!this.tag) {
      throw new Error('DeBoxChatWidget is not initialized')
    }

    this.tag.addEventListener('debox-message-pushed', callback)
    this.callbackFn = callback
  }

  public removeEventListener(callback: EventListenerOrEventListenerObject) {
    if (!this.tag) {
      throw new Error('DeBoxChatWidget is not initialized')
    }

    this.tag.removeEventListener('debox-message-pushed', callback)
    this.callbackFn = undefined
  }

  private observer?: MutationObserver
  private addToggleEvent() {
    const el = document.querySelector('debox-chat-widget')
    if (el && this.config.onToggle) {
      this.observer = new MutationObserver(() => {
        const isOpen = el.hasAttribute('open')
        this.config.onToggle?.(isOpen)
      })
      this.observer.observe(el, { attributes: true, attributeFilter: ['open'] })
    }
  }

  private removeToggleEvent() {
    this.observer?.disconnect()
  }

  private addCloseEvent() {
    this.tag?.addEventListener('debox-before-close', this.onClose)
  }

  private removeCloseEvent() {
    this.tag?.removeEventListener('debox-before-close', this.onClose)
  }

  private readonly onClose = (e: Event) => {
    this.config.onClose?.()

    if (this.config.destroyOnClose) {
      e.preventDefault()
      e.stopPropagation()

      this.destroy()
    }
  }
}

export const DeBoxChatWidget = Singleton.getInstance()
